import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Store, Phone, Mail, MapPin } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const providerCategories = ['Ingredientes', 'Empaques', 'Equipos', 'Decoracion', 'Transporte', 'Servicios', 'Otros'];
const emptyProvider = { name: '', contact_name: '', email: '', phone: '', address: '', category: 'Ingredientes', notes: '', active: true };

export default function AdminProviders() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProvider);

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  };

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/providers`, { withCredentials: true, headers: getHeaders() });
      setProviders(data);
    } catch { setProviders([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchProviders(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyProvider); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ name: p.name, contact_name: p.contact_name || '', email: p.email || '', phone: p.phone || '', address: p.address || '', category: p.category, notes: p.notes || '', active: p.active }); setDialogOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/admin/providers/${editing.id}`, form, { withCredentials: true, headers: getHeaders() });
        toast.success('Proveedor actualizado');
      } else {
        await axios.post(`${API}/admin/providers`, form, { withCredentials: true, headers: getHeaders() });
        toast.success('Proveedor creado');
      }
      setDialogOpen(false);
      fetchProviders();
    } catch { toast.error('Error al guardar'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este proveedor?')) return;
    try {
      await axios.delete(`${API}/admin/providers/${id}`, { withCredentials: true, headers: getHeaders() });
      toast.success('Eliminado');
      fetchProviders();
    } catch { toast.error('Error'); }
  };

  return (
    <div data-testid="admin-providers-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#46371c]">Proveedores</h1>
        <Button onClick={openCreate} className="bg-[#46371c] text-[#fcfbf9] hover:bg-[#2d2312] rounded-full font-body" data-testid="add-provider-btn">
          <Plus className="w-4 h-4 mr-2" /> Agregar
        </Button>
      </div>

      <Card className="border border-[#e5dfd3] bg-[#fcfbf9] overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin" /></div>
        ) : providers.length === 0 ? (
          <div className="p-12 text-center">
            <Store className="w-12 h-12 mx-auto text-[#e5dfd3] mb-4" />
            <p className="font-body text-[#75664d] mb-4">No hay proveedores registrados</p>
            <Button onClick={openCreate} className="bg-[#a0d5ba] text-[#46371c] hover:bg-[#82ba9e] rounded-full font-body" data-testid="create-first-provider">Agregar proveedor</Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table className="min-w-[650px]">
              <TableHeader>
                <TableRow className="bg-[#f4f1ea]">
                  <TableHead className="font-body text-xs font-semibold text-[#46371c]">Proveedor</TableHead>
                  <TableHead className="font-body text-xs font-semibold text-[#46371c]">Contacto</TableHead>
                  <TableHead className="font-body text-xs font-semibold text-[#46371c]">Categoria</TableHead>
                  <TableHead className="font-body text-xs font-semibold text-[#46371c]">Estado</TableHead>
                  <TableHead className="font-body text-xs font-semibold text-[#46371c]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {providers.map(p => (
                  <TableRow key={p.id} data-testid={`provider-row-${p.id}`}>
                    <TableCell>
                      <p className="font-body text-sm font-medium text-[#46371c]">{p.name}</p>
                      {p.contact_name && <p className="font-body text-xs text-[#75664d]">{p.contact_name}</p>}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-0.5">
                        {p.phone && <p className="font-body text-xs text-[#75664d] flex items-center gap-1"><Phone className="w-3 h-3" />{p.phone}</p>}
                        {p.email && <p className="font-body text-xs text-[#75664d] flex items-center gap-1"><Mail className="w-3 h-3" />{p.email}</p>}
                      </div>
                    </TableCell>
                    <TableCell><Badge className="bg-[#f4f1ea] text-[#46371c] border-0 text-xs">{p.category}</Badge></TableCell>
                    <TableCell><Badge className={p.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>{p.active ? 'Activo' : 'Inactivo'}</Badge></TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <button onClick={() => openEdit(p)} className="p-2 hover:bg-[#f4f1ea] rounded-lg"><Pencil className="w-4 h-4 text-[#75664d]" /></button>
                        <button onClick={() => handleDelete(p.id)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-400" /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-[#fcfbf9]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#46371c]">{editing ? 'Editar Proveedor' : 'Nuevo Proveedor'}</DialogTitle>
            <DialogDescription className="font-body text-sm text-[#75664d]">Datos del proveedor</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-body text-sm text-[#75664d] mb-1 block">Empresa *</label>
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="provider-name-input" />
              </div>
              <div>
                <label className="font-body text-sm text-[#75664d] mb-1 block">Contacto</label>
                <input value={form.contact_name} onChange={e => setForm(p => ({ ...p, contact_name: e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="provider-contact-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-body text-sm text-[#75664d] mb-1 block">Telefono</label>
                <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="provider-phone-input" />
              </div>
              <div>
                <label className="font-body text-sm text-[#75664d] mb-1 block">Email</label>
                <input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="provider-email-input" />
              </div>
            </div>
            <div>
              <label className="font-body text-sm text-[#75664d] mb-1 block">Categoria</label>
              <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                <SelectTrigger className="rounded-xl border-[#e5dfd3]" data-testid="provider-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{providerCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-body text-sm text-[#75664d] mb-1 block">Direccion</label>
              <input value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="provider-address-input" />
            </div>
            <div>
              <label className="font-body text-sm text-[#75664d] mb-1 block">Notas</label>
              <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} className="w-full p-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba] resize-none" data-testid="provider-notes-input" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 rounded-full border-[#e5dfd3] text-[#46371c] font-body">Cancelar</Button>
              <Button type="submit" className="flex-1 bg-[#46371c] text-[#fcfbf9] hover:bg-[#2d2312] rounded-full font-body" data-testid="save-provider-btn">Guardar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
